import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import './JqCommissionGuide.css';
import { money } from '../utils/money';

const API_BASE = '/api';
const rm = (n) => money(Number(n) || 0);
const monthLabel = (m) => new Date(m + '-01').toLocaleDateString('en-MY', { month: 'short', year: 'numeric' });

// Generalized commission guide for standard-tiered salespeople (Zac, Ginny, …).
// Adapts the JQ guide layout: standard tiered scheme has no 70/30 split, so the
// monthly columns show eligible revenue, floor status, commission and total earned.
function CommissionGuide({ month }) {
  const { id } = useParams();
  const { isAdmin, isSuperadmin } = useAuth();
  const canViewInvoices = isAdmin || isSuperadmin;
  // Year follows the top-bar month·year picker (no separate page-level selector).
  const year = (month || '2026-05').split('-')[0];
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [openMonth, setOpenMonth] = useState(null);
  const [invByMonth, setInvByMonth] = useState({}); // month -> {loading|invoices,total,count|error}
  const [recalcing, setRecalcing] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await axios.get(`${API_BASE}/commission-guide/${id}?year=${year}`, { headers: headers() });
      setData(res.data);
    } catch (e) { setError(e.response?.data?.error || 'Failed to load commission data'); }
  }, [id, year]);

  useEffect(() => { load(); }, [load]);
  // Reset any open invoice drawer when the person or year changes.
  useEffect(() => { setOpenMonth(null); setInvByMonth({}); }, [id, year]);

  const toggleInvoices = async (mk) => {
    if (openMonth === mk) { setOpenMonth(null); return; }
    setOpenMonth(mk);
    if (!invByMonth[mk]) {
      setInvByMonth((prev) => ({ ...prev, [mk]: { loading: true } }));
      try {
        const res = await axios.get(`${API_BASE}/commission-guide/${id}/invoices?month=${mk}`, { headers: headers() });
        setInvByMonth((prev) => ({ ...prev, [mk]: { invoices: res.data.invoices, total: res.data.total, count: res.data.count } }));
      } catch (e) {
        setInvByMonth((prev) => ({ ...prev, [mk]: { error: 'Failed to load invoices' } }));
      }
    }
  };

  // Re-run the commission calc for a whole month (all salespeople), refreshing
  // the stored eligible revenue to match live invoices. Superadmin only.
  const rerunMonth = async (mk) => {
    if (!window.confirm(
      `Re-run commission for ${monthLabel(mk)}?\n\n` +
      `This recalculates the WHOLE month for ALL salespeople from current invoices, ` +
      `and will update eligible revenue and commission figures. This cannot be undone automatically.\n\nContinue?`
    )) return;
    setRecalcing(true);
    try {
      await axios.post(`${API_BASE}/sync/calculate`, { month: mk }, { headers: headers() });
      await load(); // refresh the monthly table
      const res = await axios.get(`${API_BASE}/commission-guide/${id}/invoices?month=${mk}`, { headers: headers() });
      setInvByMonth((prev) => ({ ...prev, [mk]: { invoices: res.data.invoices, total: res.data.total, count: res.data.count } }));
    } catch (e) {
      alert('Recalculation failed: ' + (e.response?.data?.error || e.message));
    } finally { setRecalcing(false); }
  };

  const name = data?.salesperson?.name || '';

  if (!data) {
    return (
      <div className="jqc-page">
        <h1>Commission Guide</h1>
        {error && <div className="jqc-error">{error}</div>}
        <p className="jqc-muted">Loading…</p>
      </div>
    );
  }

  const tierPct = (r) => (parseFloat(r) * 100).toFixed(2).replace(/\.00$/, '.0') + ' %';
  // Tier bands are stored as May-base boundaries and shift with each month's floor.
  // Present them as offsets from the floor (a fixed-rate ladder).
  const baseFloor = data.tiers.length ? parseFloat(data.tiers[0].revenue_max) || 0 : 0;
  const OPEN_TOP = 999999999; // seed sentinel for an unbounded top tier (not NULL)
  const ladderRange = (t) => {
    const min = parseFloat(t.revenue_min) - baseFloor;
    const isOpenTop = !t.revenue_max || parseFloat(t.revenue_max) >= OPEN_TOP;
    const max = isOpenTop ? null : parseFloat(t.revenue_max) - baseFloor;
    if (max !== null && max <= 0) return 'Up to floor';
    if (max === null) return `Floor + ${rm(Math.max(min, 0))} and above`;
    return `Floor + ${rm(Math.max(min, 0))} – ${rm(max)}`;
  };
  // Which tier band a month's revenue lands in (bands shift with that month's floor).
  const tierForMonth = (m) => {
    if (!m.hasData || !m.meetsFloor) return null;
    const shift = (parseFloat(m.floorTarget) || 0) - baseFloor;
    for (const t of data.tiers) {
      const tmin = parseFloat(t.revenue_min) + shift;
      const tmax = (t.revenue_max && parseFloat(t.revenue_max) < OPEN_TOP) ? parseFloat(t.revenue_max) + shift : Infinity;
      if (m.eligibleRevenue >= tmin && m.eligibleRevenue <= tmax) return { level: t.tier_level, rate: parseFloat(t.rate) };
    }
    return null;
  };
  const tierCell = (m) => {
    if (!m.hasData) return '—';
    if (!m.meetsFloor) return <span className="jqc-muted">— below floor</span>;
    const ti = tierForMonth(m);
    if (!ti) return '—';
    return (
      <span style={{ background: 'rgba(47, 122, 91,0.16)', color: '#56d364', padding: '2px 8px', borderRadius: 12, fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>
        {`T${ti.level} · ${tierPct(ti.rate)}`}
      </span>
    );
  };

  const renderInvoiceDetail = (m) => {
    const st = invByMonth[m.month];
    if (!st || st.loading) return <div className="cg-inv-loading">Loading invoices…</div>;
    if (st.error) return <div className="cg-inv-loading">{st.error}</div>;
    const drift = Math.round((st.total - m.eligibleRevenue) * 100) / 100;
    return (
      <div className="cg-inv-wrap">
        <table className="cg-inv-table">
          <thead><tr><th>Date</th><th>Invoice</th><th>Customer</th><th>Venue</th><th>Amount</th></tr></thead>
          <tbody>
            {st.invoices.length ? st.invoices.map((inv, i) => (
              <tr key={i}>
                <td>{inv.date}</td>
                <td>{inv.invoiceNumber}</td>
                <td>{inv.customer || '—'}</td>
                <td>{inv.venue || '—'}</td>
                <td>{rm(inv.amount)}</td>
              </tr>
            )) : <tr><td colSpan={5} className="jqc-muted">No ready invoices for this month.</td></tr>}
            <tr className="cg-inv-sub"><td colSpan={4}>Subtotal · {st.count} ready invoice{st.count === 1 ? '' : 's'} (live)</td><td>{rm(st.total)}</td></tr>
          </tbody>
        </table>
        {Math.abs(drift) > 0.01 ? (
          <div className="cg-inv-flag">
            <div>⚠ Live invoices sum to <b>{rm(st.total)}</b>, but eligible revenue shows <b>{rm(m.eligibleRevenue)}</b> — a difference of <b>{rm(Math.abs(drift))}</b>. Invoices changed since commission was last calculated; the shown figure is a snapshot.</div>
            {isSuperadmin && (
              <button type="button" className="cg-rerun-btn" disabled={recalcing} onClick={() => rerunMonth(m.month)}>
                {recalcing ? 'Re-running…' : `↻ Re-run ${monthLabel(m.month)} to fix`}
              </button>
            )}
          </div>
        ) : (
          <div className="cg-inv-ok">✓ Live invoices match the eligible revenue exactly.</div>
        )}
      </div>
    );
  };

  return (
    <div className="jqc-page">
      <div className="jqc-head">
        <h1>Commission Guide — {name}</h1>
      </div>
      {error && <div className="jqc-error">{error}</div>}

      <div className="jqc-kpis jqc-kpis-4">
        <div className="jqc-card"><div className="label">Eligible Revenue (YTD)</div><div className="value">{rm(data.ytd.eligibleRevenue)}</div></div>
        <div className="jqc-card"><div className="label">Commission (YTD)</div><div className="value">{rm(data.ytd.commission)}</div></div>
        <div className="jqc-card"><div className="label">Basic Salary (YTD)</div><div className="value">{rm(data.ytd.basicSalary)}</div></div>
        <div className="jqc-card"><div className="label">Total Earned (YTD)</div><div className="value">{rm(data.ytd.totalEarned)}</div></div>
      </div>

      <div className="jqc-section-title">Monthly Commission (May–Dec {data.year})</div>
      <div className="report-table-scroll" role="region" aria-label="Report table — scroll to view all columns" tabIndex={0}><table className="jqc-table">
        <thead><tr><th>Month</th><th>Floor Target</th><th>Eligible Revenue</th><th>Tier</th><th>Commission</th><th>Basic Salary</th><th>Total Earned</th></tr></thead>
        <tbody>
          {data.months.map(m => (
            <React.Fragment key={m.month}>
              <tr>
                <td>{monthLabel(m.month)}</td>
                <td>{m.floorTarget ? rm(m.floorTarget) : '—'}</td>
                <td>
                  {m.hasData
                    ? (canViewInvoices
                        ? <button type="button" className="cg-inv-toggle" onClick={() => toggleInvoices(m.month)} title="Show the invoices behind this figure">{rm(m.eligibleRevenue)} <span className="cg-caret">{openMonth === m.month ? '▾' : '▸'}</span></button>
                        : rm(m.eligibleRevenue))
                    : '—'}
                </td>
                <td>{tierCell(m)}</td>
                <td>{m.hasData ? rm(m.commission) : '—'}</td>
                <td>{m.hasData ? rm(m.basicSalary) : '—'}</td>
                <td>{m.hasData ? rm(m.totalEarned) : '—'}</td>
              </tr>
              {canViewInvoices && openMonth === m.month && (
                <tr className="cg-inv-row"><td colSpan={7}>{renderInvoiceDetail(m)}</td></tr>
              )}
            </React.Fragment>
          ))}
          <tr className="total">
            <td>TOTAL</td><td></td>
            <td>{rm(data.ytd.eligibleRevenue)}</td><td></td>
            <td>{rm(data.ytd.commission)}</td><td>{rm(data.ytd.basicSalary)}</td><td>{rm(data.ytd.totalEarned)}</td>
          </tr>
        </tbody>
      </table></div>
      <div className="jqc-note">Commission is tiered on eligible monthly revenue once the floor target is met, and includes any boosters. Total Earned = commission + basic salary. Months without processed data yet show "—".</div>

      <div className="jqc-ref">
        <div>
          <div className="jqc-section-title">Tier Rate Guide</div>
          <div className="report-table-scroll" role="region" aria-label="Report table — scroll to view all columns" tabIndex={0}><table className="jqc-table">
            <thead><tr><th>Revenue band (relative to floor)</th><th>Rate</th></tr></thead>
            <tbody>{data.tiers.map(t => (
              <tr key={t.tier_level}><td>{ladderRange(t)}</td><td>{tierPct(t.rate)}</td></tr>
            ))}</tbody>
          </table></div>
          <div className="jqc-note" style={{ marginTop: 8 }}>Bands are measured from each month's floor target; only the rates are fixed.</div>
        </div>
        <div>
          <div className="jqc-section-title">Monthly Floor Targets</div>
          <div className="report-table-scroll" role="region" aria-label="Report table — scroll to view all columns" tabIndex={0}><table className="jqc-table">
            <thead><tr><th>Month</th><th>Floor</th></tr></thead>
            <tbody>{data.months.map(m => (
              <tr key={m.month}><td>{monthLabel(m.month)}</td><td>{m.floorTarget ? rm(m.floorTarget) : '—'}</td></tr>
            ))}</tbody>
          </table></div>
        </div>
      </div>
    </div>
  );
}

export default CommissionGuide;
